import Service, { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import config from 'uplisting-frontend/config/environment';
import {
  type IGaData,
  type ISegmentOptions,
  type ITrackEventParams,
} from 'ember-cli-segment';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { getGaClientId } from 'uplisting-frontend/utils/google-analytics';

export enum AnalyticsEvents {
  teamThreadOpened = 'Team_Thread_Opened',
  teamMessagePosted = 'Team_Message_Posted',
  teamMessageRemoved = 'Team_Message_Removed',
  teamTabOpened = 'Team_Tab_Opened',
  teamFilterChanged = 'Team_Filter_Changed',
  teamConversationOpened = 'Team_Conversation_Opened',
  clientManagementEnabled = 'client-management-enabled',
  clientManagementDisabled = 'client-management-disabled',
  clientStatementsDisabled = 'client-statements-disabled',
  clientStatementsEnabled = 'client-statements-enabled',

  dbePlusEnabled = 'dbe-plus-enabled',
  dbePlusDisabled = 'dbe-plus-disabled',

  edgestateAccountRemoved = 'edgestate-account-removed',
  edgestateAccountConnected = 'edgestate-account-connected',
  edgestateLockMapped = 'edgestate-lock-mapped',

  seamAccountRemoved = 'seam-account-removed',
  seamAccountConnected = 'seam-account-connected',
  seamLockMapped = 'seam-lock-mapped',

  airbnbOpportunityApplied = 'airbnb-opportunity-applied',
  airbnbOfficialConnectionAdded = 'airbnb-official-connection-added',

  connectStripe = 'connected-stripe',

  directBookingEngineEnabled = 'direct-booking-engine-enabled',

  promotionEdited = 'promotion-edit',

  propertyCreated = 'created-property',

  propertyManagementFeeSettingUpdated = 'updated-property-management-fee',

  bookingWindowRuleUpdated = 'updated-booking-window',

  automatedResponderEdit = 'automated-responder-edit',
  automatedResponderTurnedOn = 'automated-responder-turned-true',
  automatedResponderTurnedOff = 'automated-responder-turned-false',

  guestIdentityVerificationEnabled = 'guest-identity-verification-enabled',
  guestIdentityVerificationDisabled = 'guest-identity-verification-disabled',
  guestIdentityActivated = 'guest-identity-activated',

  legacyPlanUpgrade = 'legacy-plan-upgrade',

  securityDepositActivated = 'security-deposit-activated',

  customPropertyAttributeEdit = 'custom-property-attribute-edit',

  customMessageTagEdit = 'custom-message-tag-edit',

  bookingRentalAgreementSettingEdit = 'booking-rental-agreement-setting-edit',
  bookingRentalAgreementSettingEnabled = 'booking-rental-agreement-setting-enabled',
  bookingRentalAgreementSettingDisabled = 'booking-rental-agreement-setting-disabled',

  automatedReviewTemplateEdit = 'automated-review-template-edit',
  automatedReviewsActivated = 'automated-reviews-activated',
  automatedReviewsDisabled = 'automated-reviews-disabled',

  paymentRuleEdit = 'payment-rule-edit',

  messageSent = 'sent-message',
  enquirySent = 'sent-enquiry-message',

  invitationSentHost = 'invitation-sent-host',
  invitationSentOwner = 'invitation-sent-owner',
  invitationSentHousekeeper = 'invitation-sent-housekeeper',

  viewedBookingSummary = 'viewed-booking-summary',

  bookingAutomatedReviewsEnabled = 'automated-reviews-booking-enabled',
  bookingAutomatedReviewsDisabled = 'automated-reviews-booking-disabled',

  bookingSourceChanged = 'booking-source-changed',

  bookingMoved = 'moved-booking',

  updateBaseRates = 'updated-base-rates',
  updateBulkRates = 'updated-bulk-rates',
  updatedBulkMlos = 'updated-mlos',

  updatedClosedForArrival = 'updated-closed-for-arrival',
  updatedClosedForDeparture = 'updated-closed-for-departure',

  updateSpotRates = 'spot-rate-edit',

  propertyLinksApplied = 'applied-property-links',

  leadTimeRateRuleTurnedOn = 'lead-time-rate-rule-true',
  leadTimeRateRuleTurnedOff = 'lead-time-rate-rule-false',

  bdcCapturedHotelId = 'bdc_captured_hotel_id',

  channelIcal = 'channel-ical',

  taggedListing = 'tagged-listing',

  viewedOnboard = 'viewed-onboard',

  reportRequested = 'report-requested',

  appError = 'app-error',

  screenViewed = 'screen-viewed',

  // Onboarding funnel core milestones (Amplitude)
  accountCreated = 'Account_Created',
  listingImported = 'Listing_Imported',
  otaConnected = 'OTA_Connected',
  pricingRuleSet = 'Pricing_Rule_Set',
  firstMessageAutomated = 'First_Message_Automated',

  // Generic form-step funnel events
  formStarted = 'form_started',
  formSubmitted = 'form_submitted',
  formAbandoned = 'form_abandoned',

  // Sign-up step 1 (external auth) completed — user has landed on step 2 (/register/info)
  signupStep1Completed = 'signup_step1_completed',

  signupStarted = 'Signup_Started',
  registrationCompleted = 'Registration_Completed',
  signedIn = 'Signed_In',
  checkoutStarted = 'Checkout_Started',
  checkoutAbandoned = 'Checkout_Abandoned',
  billingViewed = 'Billing_Viewed',
  planIntervalToggled = 'Plan_Interval_Toggled',
  meetingViewed = 'Meeting_Viewed',
  meetingBooked = 'Meeting_Booked',
}

export default class AnalyticsService extends Service {
  @service session!: Services['session'];
  @service segment!: Services['segment'];
  @service sentry!: Services['sentry'];

  @cached
  get trackEventParams(): ITrackEventParams {
    const currentUser = this.session.currentUser as ModelRegistry['user'];

    return {
      id: currentUser?.id,
      email: currentUser?.email,
    };
  }

  private gaData: IGaData | undefined;

  public async identifyUser(): Promise<void> {
    const { currentUser } = this.session;

    if (!config.IS_PROD || !currentUser) {
      return;
    }

    if (!this.gaData) {
      await this.getGaData();
    }

    const data = {
      ...currentUser.toObject(),
      ...this.gaData,
    };

    this.segment.identifyUser(currentUser.id, data);
  }

  public trackEvent(event: AnalyticsEvents, properties = {}): void {
    this.#track(event, properties);
  }

  // Screen views fire on every route change, so they go through a dedicated
  // method rather than `trackEvent`. This keeps them out of `trackEvent` spies
  // in tests (which assert on specific user-action events) while still applying
  // the same user/GA enrichment.
  public trackScreenView(properties = {}): void {
    this.#track(AnalyticsEvents.screenViewed, properties);
  }

  // Tracking must never break a user flow — callers fire events mid-action
  // (auth callback, checkout) inside try blocks whose catch handlers are
  // destructive (logout, error notification), so failures are swallowed and
  // reported to Sentry instead of propagating.
  #track(event: AnalyticsEvents, properties: object): void {
    try {
      const segmentOptions: ISegmentOptions = {
        integrations: {
          All: true,
        },
      };

      if (this.gaData) {
        segmentOptions.traits = {
          ...this.gaData,
        };
      }

      const trackEventParams = {
        ...this.trackEventParams,
        ...properties,
      };

      this.segment.trackEvent(event, trackEventParams, segmentOptions);
    } catch (error) {
      this.sentry.captureException(error);
    }
  }

  private async getGaData(): Promise<void> {
    if (!window.gtag) {
      return;
    }

    if (!this.gaData) {
      this.gaData = {};
    }

    const sessionNumberPromise = new Promise((resolve) =>
      window.gtag('get', config.gaTag, 'session_number', resolve),
    );

    const sessionIdPromise = new Promise((resolve) =>
      window.gtag('get', config.gaTag, 'session_id', resolve),
    );

    const [sessionId, sessionNumber] = (await Promise.all([
      sessionIdPromise,
      sessionNumberPromise,
    ])) as [string, string];

    const gaClientId = getGaClientId();

    if (sessionId) {
      this.gaData.gaSessionId = sessionId;
    }

    if (sessionNumber) {
      this.gaData.gaSessionNumber = sessionNumber;
    }

    if (gaClientId) {
      this.gaData.gaClientId = gaClientId;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    analytics: AnalyticsService;
  }
}
